import { DepartureRow } from '@modules/departures/models/departureRow';
import { ArrivalRow } from '@modules/arrivals/models/arrivalRow';
import classNames from 'classnames';
import DepartureOrArrivalTableDescriptionCell from './DepartureOrArrivalTableDescriptionCell';
import DepartureOrArrivalTableDepartureOrArrivalCell from './DepartureOrArrivalTableDepartureOrArrivalCell';
import DepartureOrArrivalTablePlatformCell from './DepartureOrArrivalTablePlatformCell';
import styles from './DepartureOrArrivalTable.module.scss';
import { CSSProperties, useCallback, useMemo, useRef } from 'react';
import usePagination from '@shared/hooks/usePagination';
import useElementSize from '@shared/hooks/useElementSize';
import { MILLISECONDS_BETWEEN_DEPARTURES_OR_ARRIVALS_PAGE_CHANGE } from '@shared/constants';
import PageIndicator from '@shared/components/PageIndicator';
import { compareRows } from '@shared/utils/departureOrArrivalRowComparer';
import { DepartureOrArrivalSorting } from '@shared/models/departureOrArrivalSorting';
import PaginatedItems from './PaginatedItems';
import { DepartureOrArrivalTimeFormat } from '@shared/models/departureOrArrivalTimeFormat';
import DepartureOrArrivalTableTargetTimeCell from './DepartureOrArrivalTableTargetTimeCell';
import { getHasChangedTargetClockTime } from '@shared/utils/departureOrArrival';
import useDepartureAnnouncements from '@modules/departures/hooks/useDepartureAnnouncements';
import { DeepReadonly } from 'ts-essentials';
import DepartureOrArrivalTableChangedPlatformCell from './DepartureOrArrivalTableChangedPlatformCell';
import { getPlatformName } from '@shared/utils/platformName';
import usePausableInterval from '@shared/hooks/usePausableInterval';

interface DepartureOrArrivalTableProps {
  departureRows?: DeepReadonly<DepartureRow[]>;
  arrivalRows?: DeepReadonly<ArrivalRow[]>;
  sorting: DepartureOrArrivalSorting;
  timeFormat: DepartureOrArrivalTimeFormat;
  paging: boolean;
  showPlatform: boolean;
  onPageViewed?: (isLastPage: boolean) => void;
}

export default function DepartureOrArrivalTable({
  departureRows,
  arrivalRows,
  sorting,
  timeFormat,
  paging,
  showPlatform,
  onPageViewed,
}: DepartureOrArrivalTableProps) {
  const departuresOrArrivalsType: 'departures' | 'arrivals' = useMemo(() => {
    if (departureRows !== undefined) {
      return 'departures';
    }

    return 'arrivals';
  }, [departureRows]);
  const departureOrArrivalRows = useMemo(() => {
    if (departureRows !== undefined) {
      return departureRows;
    }

    if (arrivalRows !== undefined) {
      return arrivalRows;
    }

    return [];
  }, [departureRows, arrivalRows]);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useElementSize(containerRef);
  const tableHeadRef = useRef<HTMLTableSectionElement>(null);
  const tableHeadSize = useElementSize(tableHeadRef);
  const pageIndicatorRef = useRef<HTMLDivElement>(null);
  const pageIndicatorSize = useElementSize(pageIndicatorRef);
  const availableHeight =
    containerSize.height && tableHeadSize.height
      ? containerSize.height -
        tableHeadSize.height -
        (pageIndicatorSize.height ?? 0)
      : undefined;
  const allElementRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  const sortedDepartureOrArrivalRows = useMemo<
    DeepReadonly<(DepartureRow | ArrivalRow)[]>
  >(
    () =>
      [...departureOrArrivalRows].sort((first, second) =>
        compareRows(first, second, sorting),
      ),
    [departureOrArrivalRows, sorting],
  );

  const pageChangeSortingFunction = useCallback(() => {
    if (sorting === 'time') {
      return (
        first: DeepReadonly<DepartureRow> | DeepReadonly<ArrivalRow>,
        second: DeepReadonly<DepartureRow> | DeepReadonly<ArrivalRow>,
      ) => compareRows(first, second, sorting);
    }

    return undefined;
  }, [sorting]);

  const { itemsForCurrentPage, currentPageIndex, pages, nextPage, pageId } =
    usePagination(
      availableHeight,
      sortedDepartureOrArrivalRows,
      allElementRefs.current,
      'id',
      {
        directlyUpdateItemsWithMostCurrentItems: true,
        singlePage: !paging,
        pageChangeSortingFunction: pageChangeSortingFunction(),
      },
    );
  const isPagingTimedByPageIndicator = paging && pageId !== undefined;

  // Without a page indicator a single page is considered viewed after the same time
  usePausableInterval(
    onPageViewed ? () => onPageViewed(true) : undefined,
    isPagingTimedByPageIndicator
      ? undefined
      : MILLISECONDS_BETWEEN_DEPARTURES_OR_ARRIVALS_PAGE_CHANGE,
  );

  const departureRowsForAnnouncements = useMemo(() => {
    if (!departureRows) {
      return [];
    }

    if (!paging) {
      return itemsForCurrentPage.map(
        ([departureOrArrivalRow]) => departureOrArrivalRow,
      ) as DepartureRow[];
    }

    return departureRows;
  }, [departureRows, itemsForCurrentPage, paging]);

  const departureAnnouncements = useDepartureAnnouncements(
    departureRowsForAnnouncements,
    showPlatform,
    timeFormat,
  );

  const renderDepartureOrArrivalColumn = useMemo(() => {
    return (
      timeFormat === 'countdown' ||
      departureOrArrivalRows.some(
        (departureOrArrivalRow) =>
          getHasChangedTargetClockTime(departureOrArrivalRow.next) ||
          departureOrArrivalRow.next.isCancelled,
      )
    );
  }, [departureOrArrivalRows, timeFormat]);
  const renderTargetTimeColumn = timeFormat === 'time';
  const hasThereafter = departureOrArrivalRows.some(
    (departureOrArrivalRow) => departureOrArrivalRow.thereafter !== undefined,
  );
  const renderThereafterColumn = hasThereafter;
  const hasDeparturesOrArrivalsWithPlatform = departureOrArrivalRows.some(
    (departureOrArrivalRow) => departureOrArrivalRow.platform !== undefined,
  );
  const renderPlatformColumn =
    showPlatform && hasDeparturesOrArrivalsWithPlatform;

  const descriptionHeaderRef = useRef<HTMLTableCellElement>(null);
  const descriptionHeaderSize = useElementSize(descriptionHeaderRef);

  const shouldRenderChangedPlatform = useCallback(
    (departureOrArrivalRow: DeepReadonly<DepartureRow | ArrivalRow>) => {
      return (
        !renderPlatformColumn && departureOrArrivalRow.platform !== undefined
      );
    },
    [renderPlatformColumn],
  );
  const changedPlatformColumnSpan = useMemo(() => {
    let columns = 0;

    if (renderDepartureOrArrivalColumn) {
      columns += 1;
    }

    if (renderTargetTimeColumn) {
      columns += 1;
    }

    if (renderThereafterColumn) {
      columns += 1;
    }

    return columns;
  }, [
    renderDepartureOrArrivalColumn,
    renderTargetTimeColumn,
    renderThereafterColumn,
  ]);

  const targetTimeHeaderRef = useRef<HTMLTableCellElement>(null);
  const targetTimeHeaderSize = useElementSize(targetTimeHeaderRef, {
    renderRef: renderTargetTimeColumn,
  });

  const nextHeaderRef = useRef<HTMLTableCellElement>(null);
  const nextHeaderSize = useElementSize(nextHeaderRef, {
    renderRef: renderDepartureOrArrivalColumn,
  });

  const thereafterHeaderRef = useRef<HTMLTableCellElement>(null);
  const thereafterHeaderSize = useElementSize(thereafterHeaderRef, {
    renderRef: renderThereafterColumn,
  });

  const platformHeaderRef = useRef<HTMLTableCellElement>(null);
  const platformHeaderSize = useElementSize(platformHeaderRef, {
    renderRef: renderPlatformColumn,
  });
  const platformHeaderText = useMemo(() => {
    const platformHeaderVariants = new Array<string>();
    if (departureOrArrivalRows.some((row) => row.transportMode !== 'train')) {
      platformHeaderVariants.push(getPlatformName('bus'));
    }
    if (departureOrArrivalRows.some((row) => row.transportMode === 'train')) {
      platformHeaderVariants.push(getPlatformName('train'));
    }
    return platformHeaderVariants.join('/\u200B'); // Use a zero-width space to allow line breaks at the slash
  }, [departureOrArrivalRows]);

  const changedPlatformHeaderWidth = useMemo(() => {
    let width = 0;

    if (targetTimeHeaderSize.width !== undefined) {
      width += targetTimeHeaderSize.width;
    }

    if (nextHeaderSize.width !== undefined) {
      width += nextHeaderSize.width;
    }

    if (thereafterHeaderSize.width !== undefined) {
      width += thereafterHeaderSize.width;
    }

    return width;
  }, [
    nextHeaderSize.width,
    targetTimeHeaderSize.width,
    thereafterHeaderSize.width,
  ]);

  const dividerWidth =
    tableHeadSize.width !== undefined ? `${tableHeadSize.width}px` : undefined;

  return (
    <div
      ref={containerRef}
      className={styles['container']}
      style={{ '--divider-width': dividerWidth } as CSSProperties}
    >
      <table className={styles['all-departures-or-arrivals']}>
        <caption className="sr-only">
          {departuresOrArrivalsType === 'arrivals' ? 'Ankomster' : 'Avgångar'}
        </caption>
        <thead ref={tableHeadRef}>
          <tr>
            <th
              ref={descriptionHeaderRef}
              scope="col"
              className={styles['description']}
            >
              <span className="sr-only">Linje</span>
            </th>
            {renderDepartureOrArrivalColumn && (
              <th ref={nextHeaderRef} scope="col" className={styles.next}>
                {timeFormat === 'countdown' && (
                  <>
                    {departuresOrArrivalsType === 'arrivals'
                      ? 'Ankommer om'
                      : 'Nästa'}
                  </>
                )}
                {timeFormat === 'time' && <>Ny tid</>}
              </th>
            )}
            {renderTargetTimeColumn && (
              <th
                ref={targetTimeHeaderRef}
                scope="col"
                className={styles['next-target']}
              >
                Tid
              </th>
            )}
            {renderThereafterColumn && (
              <th
                ref={thereafterHeaderRef}
                scope="col"
                className={styles['thereafter']}
              >
                Därefter
              </th>
            )}
            {renderPlatformColumn && (
              <th
                ref={platformHeaderRef}
                scope="col"
                className={styles.platform}
              >
                {platformHeaderText}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedDepartureOrArrivalRows.map((departureOrArrivalRow) => (
            <tr
              key={departureOrArrivalRow.id}
              ref={(rowElement) => {
                if (rowElement) {
                  allElementRefs.current.set(
                    departureOrArrivalRow.id,
                    rowElement,
                  );
                } else {
                  allElementRefs.current.delete(departureOrArrivalRow.id);
                }
              }}
            >
              <DepartureOrArrivalTableDescriptionCell
                line={departureOrArrivalRow.line}
                transportMode={departureOrArrivalRow.transportMode}
                destinationOrOrigin={
                  'destination' in departureOrArrivalRow
                    ? departureOrArrivalRow.destination
                    : departureOrArrivalRow.origin
                }
                tags={
                  'tags' in departureOrArrivalRow
                    ? departureOrArrivalRow.tags
                    : undefined
                }
                timeFormat={timeFormat}
                nextTrafficSituationSeverity={
                  departureOrArrivalRow.next.trafficSituationSeverity
                }
                isNextWheelchairAccessible={
                  departureOrArrivalRow.next.isWheelchairAccessible
                }
              />
              {shouldRenderChangedPlatform(departureOrArrivalRow) && (
                <DepartureOrArrivalTableChangedPlatformCell
                  type={departuresOrArrivalsType}
                  platform={departureOrArrivalRow.platform!}
                  transportMode={departureOrArrivalRow.transportMode}
                  columnSpan={changedPlatformColumnSpan}
                />
              )}
              {renderDepartureOrArrivalColumn &&
                !shouldRenderChangedPlatform(departureOrArrivalRow) && (
                  <DepartureOrArrivalTableDepartureOrArrivalCell
                    departureOrArrival={departureOrArrivalRow.next}
                    format={timeFormat}
                  />
                )}
              {renderTargetTimeColumn &&
                !shouldRenderChangedPlatform(departureOrArrivalRow) && (
                  <DepartureOrArrivalTableTargetTimeCell
                    departureOrArrival={departureOrArrivalRow.next}
                  />
                )}
              {renderThereafterColumn &&
                !shouldRenderChangedPlatform(departureOrArrivalRow) && (
                  <DepartureOrArrivalTableDepartureOrArrivalCell
                    departureOrArrival={departureOrArrivalRow.thereafter}
                    format={timeFormat}
                  />
                )}
              {renderPlatformColumn && (
                <DepartureOrArrivalTablePlatformCell
                  originalPlatform={departureOrArrivalRow.originalPlatform}
                  platform={departureOrArrivalRow.platform}
                  transportMode={departureOrArrivalRow.transportMode}
                />
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <table
        className={styles['departures-or-arrivals']}
        style={{ marginTop: `${tableHeadSize.height}px` }}
        aria-hidden="true"
      >
        <PaginatedItems
          component="tbody"
          itemIds={itemsForCurrentPage.map(([, paginationId]) => paginationId)}
        >
          {itemsForCurrentPage.map(
            ([departureOrArrivalRow, paginationId], index) => (
              <tr
                key={paginationId}
                data-pagination-id={paginationId}
                className={classNames(
                  styles['row'],
                  index + 1 === itemsForCurrentPage.length &&
                    styles['row--last-for-page'],
                )}
              >
                <DepartureOrArrivalTableDescriptionCell
                  line={departureOrArrivalRow.line}
                  transportMode={departureOrArrivalRow.transportMode}
                  destinationOrOrigin={
                    'destination' in departureOrArrivalRow
                      ? departureOrArrivalRow.destination
                      : departureOrArrivalRow.origin
                  }
                  tags={
                    'tags' in departureOrArrivalRow
                      ? departureOrArrivalRow.tags
                      : undefined
                  }
                  timeFormat={timeFormat}
                  nextTrafficSituationSeverity={
                    departureOrArrivalRow.next.trafficSituationSeverity
                  }
                  isNextWheelchairAccessible={
                    departureOrArrivalRow.next.isWheelchairAccessible
                  }
                  headerWidth={descriptionHeaderSize.width}
                />
                {shouldRenderChangedPlatform(departureOrArrivalRow) && (
                  <DepartureOrArrivalTableChangedPlatformCell
                    type={departuresOrArrivalsType}
                    platform={departureOrArrivalRow.platform!}
                    transportMode={departureOrArrivalRow.transportMode}
                    columnSpan={changedPlatformColumnSpan}
                    headerWidth={changedPlatformHeaderWidth}
                  />
                )}
                {nextHeaderSize.width !== undefined &&
                  !shouldRenderChangedPlatform(departureOrArrivalRow) && (
                    <DepartureOrArrivalTableDepartureOrArrivalCell
                      departureOrArrival={departureOrArrivalRow.next}
                      format={timeFormat}
                      headerWidth={nextHeaderSize.width}
                    />
                  )}
                {renderTargetTimeColumn &&
                  !shouldRenderChangedPlatform(departureOrArrivalRow) && (
                    <DepartureOrArrivalTableTargetTimeCell
                      departureOrArrival={departureOrArrivalRow.next}
                      headerWidth={targetTimeHeaderSize.width}
                    />
                  )}
                {renderThereafterColumn &&
                  !shouldRenderChangedPlatform(departureOrArrivalRow) && (
                    <DepartureOrArrivalTableDepartureOrArrivalCell
                      departureOrArrival={departureOrArrivalRow.thereafter}
                      format={timeFormat}
                      headerWidth={thereafterHeaderSize.width}
                    />
                  )}
                {renderPlatformColumn && (
                  <DepartureOrArrivalTablePlatformCell
                    originalPlatform={departureOrArrivalRow.originalPlatform}
                    platform={departureOrArrivalRow.platform}
                    transportMode={departureOrArrivalRow.transportMode}
                    headerWidth={platformHeaderSize.width}
                  />
                )}
              </tr>
            ),
          )}
        </PaginatedItems>
      </table>
      <div
        className="sr-only"
        aria-label="Avgångsuppdateringar"
        role="log"
        aria-live="polite"
      >
        {departureAnnouncements.map((announcement) => (
          <p key={announcement.id}>{announcement.text}</p>
        ))}
      </div>
      {paging && (
        <div
          ref={pageIndicatorRef}
          className={styles['container__page-indicator']}
        >
          {pageId && pages > 1 && (
            <PageIndicator
              pageId={pageId}
              currentPagePaginationTime={
                MILLISECONDS_BETWEEN_DEPARTURES_OR_ARRIVALS_PAGE_CHANGE
              }
              currentPageIndex={currentPageIndex}
              pages={pages}
              onPaging={() => {
                const isLastPage = currentPageIndex >= pages - 1;
                nextPage();
                onPageViewed?.(isLastPage);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
